import {
  CompositeScreenProps,
  NavigatorScreenParams,
} from '@react-navigation/native';
import {StackScreenProps, createStackNavigator} from '@react-navigation/stack';
import React from 'react';
import {useSelector} from 'react-redux';

import {Navigators, Screens} from '../constants/navigation';
import {appFlagsSelector} from '../lib/redux/slices/appFlagsSlice';
import NavigationService from '../lib/utils/NavigationService';
import {BluetoothStartPairingScreen, PairInstructionsScreen} from '../screens';
import {
  ConnectScreen,
  type Props as ConnectScreenProps,
} from '../screens/DeviceConnect/ConnectScreen';
import {
  LimitedEditionModalScreen,
  type Props as LimitedEditionModalScreenProps,
} from '../screens/DeviceConnect/LimitedEditionModalScreen';
import {
  LimitedEditionVideoScreen,
  type Props as LimitedEditionVideoScreenProps,
} from '../screens/DeviceConnect/LimitedEditionVideoScreen';
import {colors} from '../styles';
import {
  HomeDrawerNavigator,
  type HomeDrawerParamList,
} from './HomeDrawerNavigator';
import {
  MoodLightControllerStackNavigator,
  type MoodLightControllerStackParamList,
} from './MoodLightControllerStackNavigator';
import type {RootStackScreenProps} from './RootStackNavigator';
import {
  defaultNavigationOptions,
  modalNavigationOptions,
  withBackButton,
  withSkipButton,
} from './common';
import {WithOptionalRedirect} from './params';
import {optionsToRoute, toHome} from './util';

export type MainStackParamList = {
  [Navigators.HomeDrawerNavigator]: NavigatorScreenParams<HomeDrawerParamList>;
  [Navigators.MoodLightController]: NavigatorScreenParams<MoodLightControllerStackParamList>;

  [Screens.BluetoothStartPairing]: WithOptionalRedirect | undefined;
  [Screens.PairInstructions]: WithOptionalRedirect | undefined;

  [Screens.Connect]: ConnectScreenProps | undefined;
  [Screens.LimitedEditionModal]: LimitedEditionModalScreenProps;
  [Screens.LimitedEditionVideo]: LimitedEditionVideoScreenProps | undefined;
};

type MainStackScreenProps<T extends keyof MainStackParamList> =
  CompositeScreenProps<
    StackScreenProps<MainStackParamList, T>,
    RootStackScreenProps<typeof Navigators.MainNavigator>
  >;

export type HomeDrawerNavigatorScreenProps<
  T extends keyof HomeDrawerParamList,
> = CompositeScreenProps<
  StackScreenProps<HomeDrawerParamList, T>,
  MainStackScreenProps<keyof MainStackParamList>
>;

export type MoodLightControllerNavigatorScreenProps<
  T extends keyof MoodLightControllerStackParamList,
> = CompositeScreenProps<
  StackScreenProps<MoodLightControllerStackParamList, T>,
  MainStackScreenProps<keyof MainStackParamList>
>;

const MainStack = createStackNavigator<MainStackParamList>();

export const MainStackNavigator = () => {
  const {launchCount = 0} = useSelector(appFlagsSelector);

  return (
    <MainStack.Navigator
      screenOptions={{...defaultNavigationOptions, headerShown: false}}>
      <MainStack.Screen
        name={Navigators.HomeDrawerNavigator}
        component={HomeDrawerNavigator}
      />

      <MainStack.Screen name={Screens.Connect} component={ConnectScreen} />

      <MainStack.Screen
        name={Screens.LimitedEditionModal}
        component={LimitedEditionModalScreen}
      />

      <MainStack.Screen
        name={Screens.LimitedEditionVideo}
        component={LimitedEditionVideoScreen}
      />

      <MainStack.Screen
        name={Navigators.MoodLightController}
        component={MoodLightControllerStackNavigator}
      />

      <MainStack.Screen
        name={Screens.BluetoothStartPairing}
        component={BluetoothStartPairingScreen}
        options={({route}) => {
          const redirect = route.params?.redirect;

          const resetNavigation = () => {
            NavigationService.instance()?.resetRoot({
              index: 0,
              routes: [optionsToRoute(redirect ?? toHome)],
            });
          };

          return {
            ...modalNavigationOptions,
            headerShown: true,
            ...(launchCount !== 1 && withBackButton({color: colors.black})),
            ...withSkipButton({
              onPress: launchCount === 1 ? resetNavigation : undefined,
              redirect: redirect ?? toHome,
            }),
          };
        }}
      />

      <MainStack.Screen
        name={Screens.PairInstructions}
        component={PairInstructionsScreen}
      />
    </MainStack.Navigator>
  );
};
